/**
 * Repositorio: la única puerta para escribir datos.
 *
 * Toda escritura hace DOS cosas en UNA transacción:
 *   1. guarda en IndexedDB   → la pantalla responde de inmediato
 *   2. deja el registro en la cola de subida (outbox)
 *
 * Que sean atómicas importa. Si se guardara primero y se encolara después, un
 * cierre de la app en medio dejaría un movimiento que existe en el celular
 * pero que nunca va a subir — y te darías cuenta meses después, cuando el
 * saldo del PC no cuadre con el del celular.
 *
 * REGLA: ningún componente de la interfaz llama a `db.movimientos.put(...)`
 * directamente. Todo pasa por aquí.
 */

import { db, tocar, marcarBorrado, TABLAS_SINCRONIZADAS } from './db.js';

/** Encola un registro para subirlo. Debe correr DENTRO de una transacción. */
async function encolar(tabla, registro) {
  // Si ya hay algo pendiente para este mismo registro, lo reemplazamos en vez
  // de acumular. No tiene sentido subir tres versiones de un movimiento que
  // se editó tres veces sin conexión: solo importa la última.
  await db.outbox.where('registro_id').equals(registro.id).delete();
  await db.outbox.add({
    tabla,
    registro_id: registro.id,
    intentos: 0,
    proximo_en: 0,
    encolado_en: Date.now(),
  });
}

function validarTabla(tabla) {
  if (!TABLAS_SINCRONIZADAS.includes(tabla)) {
    throw new Error(`La tabla "${tabla}" no está en la lista de sincronizadas.`);
  }
}

/**
 * Deja el registro como datos planos antes de guardarlo.
 *
 * ── POR QUÉ ESTO EXISTE ─────────────────────────────────────────────────────
 *
 * Las pantallas trabajan con estado reactivo de Svelte, que por dentro son
 * objetos "vigilados" (Proxy). IndexedDB no sabe copiar eso: rechaza el
 * registro con un DataCloneError. Pasó de verdad al corregir un pago a
 * Carolay: la pantalla mandó el pago tal como lo tenía, con su lista de
 * cuotas vigilada adentro, y la base lo rechazó sin que nadie se enterara.
 * La pantalla se quedó quieta — "no se quiere arreglar".
 *
 * Como TODA escritura pasa por aquí, aquí se arregla para todas: los
 * registros son datos de JSON (texto, números, listas), así que ir y volver
 * por JSON los deja planos sin perder nada.
 */
function plano(registro) {
  return JSON.parse(JSON.stringify(registro));
}

/** Guarda (crea o actualiza) y encola. Devuelve el registro ya sellado. */
export async function guardar(tabla, registro) {
  validarTabla(tabla);
  const sellado = tocar(plano(registro));
  await db.transaction('rw', db[tabla], db.outbox, async () => {
    await db[tabla].put(sellado);
    await encolar(tabla, sellado);
  });
  avisarCambio(tabla);
  return sellado;
}

/** Guarda varios de golpe. Una sola transacción. */
export async function guardarVarios(tabla, registros) {
  validarTabla(tabla);
  if (!registros.length) return [];
  const sellados = registros.map((r) => tocar(plano(r)));
  await db.transaction('rw', db[tabla], db.outbox, async () => {
    await db[tabla].bulkPut(sellados);
    for (const r of sellados) await encolar(tabla, r);
  });
  avisarCambio(tabla);
  return sellados;
}

/**
 * Borra. Ojo: NO elimina la fila, la marca como borrada.
 *
 * Si se borrara de verdad, el otro dispositivo no tendría forma de enterarse:
 * al sincronizar vería que a este le falta un registro que él sí tiene, y lo
 * volvería a mandar. El movimiento resucitaría. Por eso se marca y se guarda
 * la marca — es lo que en jerga se llama una "lápida".
 */
export async function borrar(tabla, id) {
  validarTabla(tabla);
  const actual = await db[tabla].get(id);
  if (!actual) return null;
  const muerto = marcarBorrado(actual);
  await db.transaction('rw', db[tabla], db.outbox, async () => {
    await db[tabla].put(muerto);
    await encolar(tabla, muerto);
  });
  avisarCambio(tabla);
  return muerto;
}

/**
 * Aplica lo que llegó del servidor.
 *
 * Esto NO se encola: viene de afuera, no hay que devolverlo. Encolarlo crearía
 * un ciclo infinito de sincronización entre los dos dispositivos.
 *
 * Resolución de conflicto: gana el que tenga `actualizado_en` mayor. Si
 * empatan al milisegundo, desempata el id del dispositivo por orden
 * alfabético — arbitrario, pero los dos dispositivos llegan a la misma
 * conclusión sin necesidad de hablar entre ellos.
 */
export async function aplicarDelta(delta) {
  const resumen = {};
  for (const [tabla, registros] of Object.entries(delta || {})) {
    if (!TABLAS_SINCRONIZADAS.includes(tabla) || !registros?.length) continue;

    let aplicados = 0;
    let descartados = 0;

    await db.transaction('rw', db[tabla], async () => {
      for (const remoto of registros) {
        const local = await db[tabla].get(remoto.id);
        if (!local || ganaRemoto(local, remoto)) {
          await db[tabla].put(remoto);
          aplicados++;
        } else {
          descartados++;
        }
      }
    });

    resumen[tabla] = { aplicados, descartados };
    if (aplicados) avisarCambio(tabla);
  }
  return resumen;
}

function ganaRemoto(local, remoto) {
  const tLocal = Number(local.actualizado_en || 0);
  const tRemoto = Number(remoto.actualizado_en || 0);
  if (tRemoto !== tLocal) return tRemoto > tLocal;
  return String(remoto.dispositivo || '') > String(local.dispositivo || '');
}

// ─── Lecturas ───────────────────────────────────────────────────────────────
// Todas filtran los borrados. Es tan fácil olvidarlo que mejor que no exista
// una lectura cruda: si alguna vista muestra movimientos borrados, el saldo
// va a estar mal y nadie va a entender por qué.

export function vivos(coleccion) {
  return coleccion.filter((r) => !r.borrado_en);
}

export async function listar(tabla) {
  return vivos(db[tabla].toCollection()).toArray();
}

export async function obtener(tabla, id) {
  const r = await db[tabla].get(id);
  return r && !r.borrado_en ? r : null;
}

/** Movimientos de un rango de fechas, del más nuevo al más viejo. */
export async function movimientosEntre(desde, hasta) {
  const lista = await db.movimientos
    .where('fecha').between(desde, hasta, true, true)
    .toArray();
  return lista
    .filter((m) => !m.borrado_en)
    .sort((a, b) => (b.fecha + (b.hora ?? '')).localeCompare(a.fecha + (a.hora ?? '')));
}

/**
 * Saldo de una cuenta.
 *
 *   saldo = saldo_inicial + todo lo que entró − todo lo que salió
 *
 * Una transferencia aparece como salida en una cuenta y entrada en la otra,
 * así que el patrimonio total no cambia. Ese es justo el punto.
 */
export async function saldoCuenta(cuentaId) {
  const cuenta = await db.cuentas.get(cuentaId);
  if (!cuenta) return 0;

  const [entradas, salidas] = await Promise.all([
    db.movimientos.where('cuenta_destino').equals(cuentaId).toArray(),
    db.movimientos.where('cuenta_origen').equals(cuentaId).toArray(),
  ]);

  const suma = (lista) => lista
    .filter((m) => !m.borrado_en)
    .reduce((t, m) => t + (Number(m.monto) || 0), 0);

  return (Number(cuenta.saldo_inicial) || 0) + suma(entradas) - suma(salidas);
}

/** Cuántos cambios están esperando subir. Alimenta el indicador de la interfaz. */
export async function pendientesDeSubir() {
  return db.outbox.count();
}

// ─── Aviso de cambios ───────────────────────────────────────────────────────
// La interfaz escucha esto para refrescarse. Es más simple que un store
// global y funciona igual entre pestañas cuando se necesite.

const oyentes = new Set();

export function alCambiar(fn) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

function avisarCambio(tabla) {
  for (const fn of oyentes) {
    try {
      fn(tabla);
    } catch (e) {
      console.error('Un oyente de cambios falló:', e);
    }
  }
}
